import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useDrawerEditor } from '@/features/sessions/components/workspace/DetailDrawer/SessionPanel/DrawerEditorContext';
import { cn } from '@/lib/utils';

import { BubbleToolbar } from './BubbleToolbar';
import { EditorDocumentHeader } from './EditorDocumentHeader';
import { EditorToolbar } from './EditorToolbar';
import { ShortcutHintOverlay } from './ShortcutHintOverlay';
import { buildEditorExtensions } from './extensions';

export type EditorKind = 'note' | 'case-report';
export type AutosaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

export interface EditorDocument {
  id: string;
  content: string | null;
  status: string;
  updatedAt?: string | null;
}

export interface DocumentMeta {
  clientName: string;
  scheduledStart: string | null;
  authorLabel: string;
}

interface Props {
  kind: EditorKind;
  document: EditorDocument | null;
  isReadOnly: boolean;
  onAutosave: (content: string) => Promise<void>;
  topbar: (ctx: ShellContext) => ReactNode;
  footer: (ctx: ShellContext) => ReactNode;
  sidePanel: ReactNode;
  /** Drives the in-document editorial header (title + meta line). */
  documentMeta: DocumentMeta;
}

export interface ShellContext {
  editor: Editor | null;
  autosaveState: AutosaveState;
  wordCount: number;
  characterCount: number;
  focusMode: boolean;
  toggleFocusMode: () => void;
  forceSave: () => void;
}

const FOCUS_MODE_KEY = 'editor.focusMode';
const AUTOSAVE_DEBOUNCE_MS = 2000;
const SAVED_LINGER_MS = 5000;

export function ClinicalEditorShell({
  kind,
  document,
  isReadOnly,
  onAutosave,
  topbar,
  footer,
  sidePanel,
  documentMeta,
}: Props) {
  const drawerEditor = useDrawerEditor();
  const inDrawer = drawerEditor !== null;
  const [autosaveState, setAutosaveState] = useState<AutosaveState>('idle');
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    return globalThis.localStorage.getItem(FOCUS_MODE_KEY) === 'true';
  });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedLingerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onAutosaveRef = useRef(onAutosave);
  useEffect(() => {
    onAutosaveRef.current = onAutosave;
  });

  const placeholder = 'Start writing — type / for commands';
  const extensions = useMemo(() => buildEditorExtensions(placeholder, kind), [kind]);

  // `immediatelyRender: false` keeps the hook returning `Editor | null` so the
  // shell context can pass through a stable nullable type to slot consumers.
  const editor = useEditor(
    {
      extensions,
      content: document?.content ?? '',
      editable: !isReadOnly,
      immediatelyRender: false,
      onUpdate: ({ editor: ed }) => {
        setAutosaveState('dirty');
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          const html = ed.getHTML();
          setAutosaveState('saving');
          onAutosaveRef
            .current(html)
            .then(() => {
              setAutosaveState('saved');
              if (savedLingerRef.current) clearTimeout(savedLingerRef.current);
              savedLingerRef.current = setTimeout(() => {
                setAutosaveState('idle');
              }, SAVED_LINGER_MS);
            })
            .catch(() => {
              setAutosaveState('failed');
            });
        }, AUTOSAVE_DEBOUNCE_MS);
      },
    },
    [extensions, document?.id, isReadOnly],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedLingerRef.current) clearTimeout(savedLingerRef.current);
    };
  }, []);

  // Cancel any in-flight autosave debounce when the document being edited
  // changes — prevents stale content from being written to the new document.
  // Capture the id so the dep is read inside the effect body, keeping
  // exhaustive-deps satisfied without a suppress directive.
  const currentDocId = document?.id;
  useEffect(() => {
    void currentDocId;
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [currentDocId]);

  useEffect(() => {
    globalThis.localStorage.setItem(FOCUS_MODE_KEY, focusMode ? 'true' : 'false');
  }, [focusMode]);

  const toggleFocusMode = useCallback(() => {
    setFocusMode((prev) => !prev);
  }, []);

  const forceSave = useCallback(() => {
    if (!editor) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const html = editor.getHTML();
    setAutosaveState('saving');
    void onAutosaveRef
      .current(html)
      .then(() => {
        setAutosaveState('saved');
      })
      .catch(() => {
        setAutosaveState('failed');
      });
  }, [editor]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isInTextField =
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');
      const inEditor = e.target instanceof HTMLElement && e.target.closest('.ProseMirror') !== null;

      // ⌘+S force save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        forceSave();
        return;
      }
      // ⌘+. toggle focus mode
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        toggleFocusMode();
        return;
      }
      // ? overlay (only when not in a text field / editor)
      if (e.key === '?' && !isInTextField && !inEditor && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [forceSave, toggleFocusMode]);

  const wordCount = editor?.storage.characterCount.words() ?? 0;
  const characterCount = editor?.storage.characterCount.characters() ?? 0;

  const ctx: ShellContext = {
    editor,
    autosaveState,
    wordCount,
    characterCount,
    focusMode,
    toggleFocusMode,
    forceSave,
  };

  // react-hooks/refs (v7) infers that `ctx.editor` is ref-backed and flags
  // passing it through a render-time function call as a ref-read-during-render.
  // This is a known false positive (facebook/react#35575): `editor` is not a
  // raw ref but an object returned by useEditor that the rule over-infers.
  // The calls are safe: topbar/footer are pure render functions; editor is not
  // a DOM ref being read for layout. Suppress at both call sites.
  return (
    // Outer canvas. In the drawer the parent Sheet already owns the
    // elevation, so we go flush — no tinted page, no card frame. On a
    // standalone route the editor sits on a soft brand-tinted page and
    // gets a restrained card frame: subtle radius, hairline border, no
    // dramatic shadow.
    <div className={cn('flex h-full flex-col', inDrawer ? 'bg-white' : 'bg-background p-4')}>
      <div
        // overflow-hidden clips topbar/footer/sticky chrome; flex-col +
        // min-h-0 keeps the inner scroll container sized correctly inside
        // the frame regardless of mode.
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden bg-white',
          inDrawer
            ? ''
            : 'border-accent-ink/10 rounded-lg border shadow-[0_2px_8px_-4px_rgba(3,68,92,0.06)]',
        )}
      >
        {/* eslint-disable-next-line react-hooks/refs */}
        {topbar(ctx)}
        {/* The persistent toolbar stays visible in focus mode — the side rail
            disappears, but formatting controls are still useful while writing.
            Only hides when the editor is read-only (sealed/submitted). */}
        {!isReadOnly ? <EditorToolbar editor={editor} kind={kind} /> : null}
        <div className="flex flex-1 overflow-hidden">
          <main
            className={cn(
              // The prose viewport is its own page-tinted column. The thin border
              // on the right separates the writing surface from the side rail
              // without dropping a heavy shadow.
              'flex flex-1 flex-col overflow-y-auto bg-white transition-all motion-reduce:transition-none',
            )}
          >
            <div
              className={cn(
                'mx-auto w-full px-8 pt-10 pb-24 transition-all motion-reduce:transition-none',
                focusMode ? 'max-w-190' : 'max-w-180',
              )}
            >
              <EditorDocumentHeader
                kind={kind}
                clientName={documentMeta.clientName}
                scheduledStart={documentMeta.scheduledStart}
                authorLabel={documentMeta.authorLabel}
                autosaveState={autosaveState}
                wordCount={wordCount}
              />
              {!isReadOnly ? <BubbleToolbar editor={editor} /> : null}
              <EditorContent
                editor={editor}
                className={cn(
                  'prose-editorial-doc focus-within:outline-none',
                  isReadOnly && 'text-neutral-700',
                )}
              />
            </div>
          </main>
          {focusMode ? null : (
            <aside className="border-accent-ink/10 flex w-90 shrink-0 flex-col gap-3 overflow-y-auto border-l bg-neutral-50/40 p-5">
              {sidePanel}
            </aside>
          )}
        </div>
        {/* eslint-disable-next-line react-hooks/refs */}
        {footer(ctx)}
      </div>
      <ShortcutHintOverlay open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
}
